import { Component, input, output, signal, computed, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Select Option
 */
export interface SelectOption {
  value: any;
  label: string;
  disabled?: boolean;
  icon?: string;
  description?: string;
}

/**
 * Select Component
 * 
 * Dropdown select component with search, keyboard navigation, and accessibility.
 * 
 * @example
 * ```typescript
 * // In your component
 * selectedValue = signal('');
 * 
 * options: SelectOption[] = [
 *   { value: 'opt1', label: 'Opção 1', icon: '📄' },
 *   { value: 'opt2', label: 'Opção 2', description: 'Descrição da opção' },
 *   { value: 'opt3', label: 'Opção 3', disabled: true }
 * ];
 * ```
 * 
 * ```html
 * <mg3p-select
 *   label="Selecione uma opção"
 *   [options]="options"
 *   [value]="selectedValue()"
 *   (valueChange)="selectedValue.set($event)"
 *   [searchable]="true"
 *   placeholder="Escolha..."
 * />
 * ```
 * 
 * Features:
 * - Searchable options
 * - Keyboard navigation (Arrow keys, Enter, Esc)
 * - Option icons and descriptions
 * - Disabled options
 * - Empty state
 * - Clear button
 * - WCAG 2.1 Level AA compliant
 */
@Component({
  selector: 'mg3p-select',
  standalone: true,
  imports: [CommonModule],
  host: {
    class: 'mg3p-select-host',
    '[class.mg3p-select-host--open]': 'isOpen()',
  },
  template: `
    <div 
      class="select"
      [class.select--open]="isOpen()"
      [class.select--disabled]="disabled()"
      [class.select--error]="error()"
    >
      <!-- Label -->
      @if (label()) {
        <label 
          [for]="selectId()"
          class="select__label"
        >
          {{ label() }}
          @if (required()) {
            <span class="select__required" aria-label="obrigatório">*</span>
          }
        </label>
      }

      <!-- Select trigger -->
      <button
        type="button"
        [id]="selectId()"
        class="select__trigger"
        (click)="toggle()"
        [disabled]="disabled()"
        [attr.aria-haspopup]="'listbox'"
        [attr.aria-expanded]="isOpen()"
        [attr.aria-labelledby]="label() ? selectId() + '-label' : null"
        [attr.aria-describedby]="helpText() ? selectId() + '-help' : null"
      >
        <span class="select__value">
          @if (selectedOption()) {
            @if (selectedOption()!.icon) {
              <span class="select__icon" aria-hidden="true">{{ selectedOption()!.icon }}</span>
            }
            <span>{{ selectedOption()!.label }}</span>
          } @else {
            <span class="select__placeholder">{{ placeholder() }}</span>
          }
        </span>

        <span class="select__arrow" aria-hidden="true">
          {{ isOpen() ? '▲' : '▼' }}
        </span>

        @if (clearable() && selectedOption() && !disabled()) {
          <button
            type="button"
            class="select__clear"
            (click)="clear($event)"
            [attr.aria-label]="'Limpar seleção'"
          >
            <span aria-hidden="true">✕</span>
          </button>
        }
      </button>

      <!-- Dropdown -->
      @if (isOpen()) {
        <div class="select__dropdown" role="listbox">
          <!-- Search -->
          @if (searchable()) {
            <div class="select__search">
              <input
                #searchInput
                type="text"
                class="select__search-input"
                [value]="searchQuery()"
                (input)="onSearchInput($event)"
                placeholder="Buscar..."
                [attr.aria-label]="'Buscar opções'"
              />
            </div>
          }

          <!-- Options -->
          <div class="select__options">
            @if (filteredOptions().length > 0) {
              @for (option of filteredOptions(); track option.value) {
                <button
                  type="button"
                  class="select__option"
                  [class.select__option--selected]="isSelected(option)"
                  [class.select__option--disabled]="option.disabled"
                  [class.select__option--highlighted]="isHighlighted(option)"
                  (click)="selectOption(option)"
                  (mouseenter)="highlightOption(option)"
                  [disabled]="option.disabled"
                  role="option"
                  [attr.aria-selected]="isSelected(option)"
                  [attr.aria-disabled]="option.disabled"
                >
                  @if (option.icon) {
                    <span class="select__option-icon" aria-hidden="true">{{ option.icon }}</span>
                  }
                  <div class="select__option-content">
                    <div class="select__option-label">{{ option.label }}</div>
                    @if (option.description) {
                      <div class="select__option-description">{{ option.description }}</div>
                    }
                  </div>
                  @if (isSelected(option)) {
                    <span class="select__check" aria-hidden="true">✓</span>
                  }
                </button>
              }
            } @else {
              <div class="select__empty">
                <span class="select__empty-icon" aria-hidden="true">🔍</span>
                <div class="select__empty-text">
                  {{ searchQuery() ? 'Nenhuma opção encontrada' : 'Nenhuma opção disponível' }}
                </div>
              </div>
            }
          </div>
        </div>
      }

      <!-- Help text / Error message -->
      @if (helpText() || errorMessage()) {
        <div class="select__footer">
          @if (errorMessage()) {
            <div 
              [id]="selectId() + '-error'"
              class="select__error-message"
              role="alert"
            >
              <span aria-hidden="true">⚠️</span>
              {{ errorMessage() }}
            </div>
          } @else if (helpText()) {
            <div 
              [id]="selectId() + '-help'"
              class="select__help-text"
            >
              {{ helpText() }}
            </div>
          }
        </div>
      }
    </div>

    <!-- Backdrop -->
    @if (isOpen()) {
      <div 
        class="select__backdrop"
        (click)="close()"
        aria-hidden="true"
      ></div>
    }
  `,
  styles: [`
    :host {
      display: block;
      position: relative;
    }

    /* Eleva o host (dropdown + backdrop) acima dos irmãos no fluxo (ex.: wizard) */
    :host(.mg3p-select-host--open) {
      z-index: var(--z-dropdown);
    }

    .select {
      position: relative;
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xs);
    }

    .select--open {
      z-index: 1;
    }

    .select__label {
      font-size: var(--font-size-sm);
      font-weight: var(--font-weight-medium);
      color: var(--color-text-primary);
    }

    .select__required {
      color: var(--color-danger);
      margin-left: var(--spacing-xs);
    }

    .select__trigger {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--spacing-sm);
      padding: var(--spacing-sm) var(--spacing-md);
      font-size: var(--font-size-md);
      color: var(--color-text-primary);
      background: var(--color-surface);
      border: 1px solid var(--color-border);
      border-radius: var(--radius-md);
      cursor: pointer;
      transition: all var(--transition-fast);
      text-align: left;
    }

    .select__trigger:hover:not(:disabled) {
      border-color: var(--color-border-hover);
    }

    .select__trigger:focus-visible {
      outline: 2px solid var(--color-primary);
      outline-offset: 2px;
    }

    .select__trigger:disabled {
      background: var(--color-surface-secondary);
      color: var(--color-text-tertiary);
      cursor: not-allowed;
    }

    .select--open .select__trigger {
      border-color: var(--color-primary);
      box-shadow: 0 0 0 3px var(--color-primary-light);
    }

    .select__value {
      display: flex;
      align-items: center;
      gap: var(--spacing-sm);
      flex: 1;
      min-width: 0;
    }

    .select__placeholder {
      color: var(--color-text-tertiary);
    }

    .select__icon,
    .select__option-icon {
      font-size: var(--font-size-lg);
      flex-shrink: 0;
    }

    .select__arrow {
      font-size: var(--font-size-sm);
      color: var(--color-text-tertiary);
      transition: transform var(--transition-fast);
    }

    .select__clear {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      padding: 0;
      background: transparent;
      border: none;
      border-radius: var(--radius-sm);
      color: var(--color-text-tertiary);
      cursor: pointer;
      transition: all var(--transition-fast);
    }

    .select__clear:hover {
      background: var(--color-surface-hover);
      color: var(--color-text-primary);
    }

    .select__dropdown {
      position: absolute;
      top: calc(100% + var(--spacing-xs));
      left: 0;
      right: 0;
      background: var(--color-surface);
      border: 1px solid var(--color-border);
      border-radius: var(--radius-md);
      box-shadow: var(--shadow-lg);
      z-index: var(--z-dropdown);
      max-height: 300px;
      display: flex;
      flex-direction: column;
      animation: slideDown var(--transition-fast);
    }

    .select__search {
      padding: var(--spacing-sm);
      border-bottom: 1px solid var(--color-border);
    }

    .select__search-input {
      width: 100%;
      padding: var(--spacing-sm);
      font-size: var(--font-size-sm);
      color: var(--color-text-primary);
      background: var(--color-surface-secondary);
      border: 1px solid var(--color-border);
      border-radius: var(--radius-sm);
      outline: none;
    }

    .select__search-input:focus {
      border-color: var(--color-primary);
    }

    .select__options {
      overflow-y: auto;
      overflow-x: hidden;
      padding: var(--spacing-xs);
    }

    .select__option {
      display: flex;
      align-items: center;
      gap: var(--spacing-sm);
      width: 100%;
      padding: var(--spacing-sm) var(--spacing-md);
      background: transparent;
      border: none;
      border-radius: var(--radius-sm);
      cursor: pointer;
      transition: all var(--transition-fast);
      text-align: left;
    }

    .select__option:hover:not(:disabled) {
      background: var(--color-surface-hover);
    }

    .select__option--highlighted {
      background: var(--color-surface-hover);
    }

    .select__option--selected {
      background: var(--color-primary-light);
      color: var(--color-primary);
    }

    .select__option--disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .select__option:focus-visible {
      outline: 2px solid var(--color-primary);
      outline-offset: -2px;
    }

    .select__option-content {
      flex: 1;
      min-width: 0;
    }

    .select__option-label {
      font-size: var(--font-size-md);
      color: var(--color-text-primary);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .select__option-description {
      font-size: var(--font-size-sm);
      color: var(--color-text-secondary);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .select__check {
      color: var(--color-primary);
      font-size: var(--font-size-lg);
    }

    .select__empty {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: var(--spacing-xl);
      text-align: center;
    }

    .select__empty-icon {
      font-size: 32px;
      opacity: 0.3;
      margin-bottom: var(--spacing-sm);
    }

    .select__empty-text {
      font-size: var(--font-size-sm);
      color: var(--color-text-secondary);
    }

    .select__footer {
      display: flex;
      gap: var(--spacing-sm);
    }

    .select__help-text {
      font-size: var(--font-size-sm);
      color: var(--color-text-secondary);
    }

    .select__error-message {
      display: flex;
      align-items: center;
      gap: var(--spacing-xs);
      font-size: var(--font-size-sm);
      color: var(--color-danger);
    }

    .select__backdrop {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: calc(var(--z-dropdown) - 1);
    }

    /* Error state */
    .select--error .select__trigger {
      border-color: var(--color-danger);
    }

    /* Animations */
    @keyframes slideDown {
      from {
        opacity: 0;
        transform: translateY(-8px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    /* Reduced motion */
    @media (prefers-reduced-motion: reduce) {
      .select__trigger,
      .select__arrow,
      .select__option,
      .select__dropdown {
        animation: none;
        transition: none;
      }
    }
  `]
})
export class SelectComponent {
  // Inputs
  label = input<string>('');
  options = input.required<SelectOption[]>();
  value = input<any>(null);
  placeholder = input<string>('Selecione...');
  helpText = input<string>('');
  errorMessage = input<string>('');
  required = input<boolean>(false);
  disabled = input<boolean>(false);
  searchable = input<boolean>(false);
  clearable = input<boolean>(false);
  error = input<boolean>(false);

  // Outputs
  valueChange = output<any>();

  // State
  isOpen = signal<boolean>(false);
  searchQuery = signal<string>('');
  highlightedIndex = signal<number>(-1);
  selectId = signal<string>(`select-${Math.random().toString(36).substr(2, 9)}`);

  // Computed
  selectedOption = computed(() => {
    return this.options().find(opt => opt.value === this.value());
  });

  filteredOptions = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    
    if (!query) {
      return this.options();
    }

    return this.options().filter(opt => 
      opt.label.toLowerCase().includes(query) ||
      opt.description?.toLowerCase().includes(query)
    );
  });

  highlightedOption = computed(() => {
    const index = this.highlightedIndex();
    return index >= 0 ? this.filteredOptions()[index] : null;
  });

  @HostListener('document:keydown', ['$event'])
  handleKeyboard(event: KeyboardEvent): void {
    if (!this.isOpen()) return;

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.close();
        break;

      case 'ArrowDown':
        event.preventDefault();
        this.highlightNext();
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.highlightPrevious();
        break;

      case 'Enter':
        event.preventDefault();
        const highlighted = this.highlightedOption();
        if (highlighted && !highlighted.disabled) {
          this.selectOption(highlighted);
        }
        break;
    }
  }

  toggle(): void {
    if (this.disabled()) return;
    
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  open(): void {
    this.isOpen.set(true);
    this.searchQuery.set('');
    this.highlightedIndex.set(-1);

    // Auto-focus search input if searchable
    if (this.searchable()) {
      setTimeout(() => {
        const input = document.querySelector('.select__search-input') as HTMLInputElement;
        input?.focus();
      }, 100);
    }
  }

  close(): void {
    this.isOpen.set(false);
    this.searchQuery.set('');
    this.highlightedIndex.set(-1);
  }

  selectOption(option: SelectOption): void {
    if (option.disabled) return;
    
    this.valueChange.emit(option.value);
    this.close();
  }

  clear(event: Event): void {
    event.stopPropagation();
    this.valueChange.emit(null);
  }

  onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchQuery.set(input.value);
    this.highlightedIndex.set(0);
  }

  highlightOption(option: SelectOption): void {
    const index = this.filteredOptions().indexOf(option);
    this.highlightedIndex.set(index);
  }

  highlightNext(): void {
    const options = this.filteredOptions();
    const currentIndex = this.highlightedIndex();
    let nextIndex = currentIndex + 1;

    // Skip disabled options
    while (nextIndex < options.length && options[nextIndex].disabled) {
      nextIndex++;
    }

    if (nextIndex < options.length) {
      this.highlightedIndex.set(nextIndex);
      this.scrollToHighlighted();
    }
  }

  highlightPrevious(): void {
    const options = this.filteredOptions();
    const currentIndex = this.highlightedIndex();
    let prevIndex = currentIndex - 1;

    // Skip disabled options
    while (prevIndex >= 0 && options[prevIndex].disabled) {
      prevIndex--;
    }

    if (prevIndex >= 0) {
      this.highlightedIndex.set(prevIndex);
      this.scrollToHighlighted();
    }
  }

  isSelected(option: SelectOption): boolean {
    return option.value === this.value();
  }

  isHighlighted(option: SelectOption): boolean {
    return this.highlightedOption() === option;
  }

  private scrollToHighlighted(): void {
    setTimeout(() => {
      const highlighted = document.querySelector('.select__option--highlighted');
      highlighted?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 0);
  }
}

// Made with Bob
